import { BaseQueryT } from "../../api";
import * as React from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store.ts";
import { addNewSlice, AddNewTarget } from "../../redux/slices/addNew.ts";
import { useSetDataByView } from "../../hooks/useSetDataByView/useSetDataByView.ts";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { TypedMutationTrigger } from "@reduxjs/toolkit/query/react";
import { extractApiError } from "../../api/errors.ts";

import { useShowToast } from "../../contexts/ToastContext.tsx";

export const addOptionDialogFactory = <
  FormT extends Record<string, unknown>,
  MutationArgT extends Record<string, unknown>,
  MutationResultT extends { id: number },
  FormPropsT extends {
    formContent: FormT;
    setFormContent: React.Dispatch<React.SetStateAction<FormT>>;
  } = {
    formContent: FormT;
    setFormContent: React.Dispatch<React.SetStateAction<FormT>>;
  },
>({
  defaultValues,
  FormComponent,
  useMutation,
  createMutationArg,
  validate,
  headerText,
  makeFormProps = () => ({}),
}: {
  defaultValues: FormT;
  FormComponent: React.FC<
    Pick<FormPropsT, "formContent" | "setFormContent"> &
      Partial<Omit<FormPropsT, "formContent" | "setFormContent">>
  >;
  useMutation: () => Readonly<
    [
      TypedMutationTrigger<MutationResultT, MutationArgT, BaseQueryT>,
      Record<string, unknown>,
    ]
  >;
  createMutationArg: (
    formContent: FormT,
    rowId: number | undefined,
    field: string,
  ) => MutationArgT;
  validate: (formContent: Partial<FormT>) => boolean;
  headerText: string;
  makeFormProps?: (props: {
    rowId: number | undefined;
    field: string;
    initialValues?: Partial<FormT>;
  }) => Partial<Omit<FormPropsT, "formContent" | "setFormContent">>;
}) => {
  const AddOptionDialog = <FieldTypeT extends string>({
    field,
    rowId,
    initialValues,
  }: {
    rowId: number | undefined;
    field: FieldTypeT;
    initialValues?: Partial<FormT>;
  }) => {
    const dispatch = useAppDispatch();
    const { isOpen } = useAppSelector((state) => state.createForm);
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [trigger] = useMutation();
    const [formContent, setFormContent] = React.useState<FormT>({
      ...defaultValues,
      ...(initialValues ?? {}),
    });
    const onHide = React.useCallback(() => {
      setSubmitting(false);
      dispatch(addNewSlice.actions.closeAddNewDialog());
    }, [dispatch]);
    const { setData } = useSetDataByView();
    const isValid = React.useMemo(() => validate(formContent), [formContent]);
    const showToast = useShowToast();
    const onSave = React.useCallback(async () => {
      if (!isValid) {
        return;
      }
      setSubmitting(true);
      const { error, data } = await trigger(
        createMutationArg(formContent, rowId, field),
      );
      if (error) {
        console.error(error);
        showToast({
          severity: "error",
          summary: "Błąd",
          detail: extractApiError(error),
        });
        setSubmitting(false);
        return;
      }
      const { id: createdId } = data;
      dispatch(addNewSlice.actions.setLastAdded({ value: createdId }));

      if (!setData) {
        setSubmitting(false);
        return;
      }
      // ToDo: Here we should handle errors as well, but setData type such a mess at this state we'll do it later when it is more defined
      if (!isOpen) {
        await setData([
          {
            id: rowId,
            [field]: createdId,
          },
        ]);
      }
      dispatch(addNewSlice.actions.closeAddNewDialog());
      setSubmitting(false);
    }, [
      trigger,
      isValid,
      formContent,
      setData,
      rowId,
      field,
      dispatch,
      showToast,
      isOpen,
    ]);

    return (
      <Dialog
        header={headerText}
        visible
        onHide={onHide}
        breakpoints={{ "2000px": "40vw", "960px": "75vw", "641px": "95vw" }}
        style={{ width: "50vw" }}
        footer={
          <>
            <Button
              label="Anuluj"
              className="p-button-text"
              onClick={onHide}
              disabled={isSubmitting}
            />
            <Button
              label="Zapisz"
              icon="pi pi-check"
              onClick={() => {
                void onSave();
              }}
              disabled={isSubmitting || !isValid}
            />
          </>
        }
      >
        <FormComponent
          {...makeFormProps({ rowId, field, initialValues })}
          formContent={formContent}
          setFormContent={setFormContent}
        />
      </Dialog>
    );
  };
  AddOptionDialog.displayName = "AddOptionDialog";
  return AddOptionDialog;
};

export const isAddNewAddressTarget = (
  target: AddNewTarget | undefined | null,
): target is AddNewTarget<"address"> => !!target && target.type === "address";

export const isAddNewCompanyTarget = (
  target: AddNewTarget | undefined | null,
): target is AddNewTarget<"company"> => !!target && target.type === "company";

export const isAddNewProductTarget = (
  target: AddNewTarget | undefined | null,
): target is AddNewTarget<"product"> => !!target && target.type === "product";

export const isAddNewDriverTarget = (
  target: AddNewTarget | undefined | null,
): target is AddNewTarget<"driver"> => !!target && target.type === "driver";

export const isAddNewVehicleTarget = (
  target: AddNewTarget | undefined | null,
): target is AddNewTarget<"vehicle"> => !!target && target.type === "vehicle";
